/**
 * Module access card for the role permissions builder.
 */
import React from 'react';
import { DynamicIcon } from '@/components/forms/IconPicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { Plus } from 'lucide-react';
import { RuleCard } from './RuleCard';
import { RoleTabPermCard } from './RoleTabPermCard';
import { ReadScopeBuilder } from './ReadScopeBuilder';
import { NaturalLanguagePreview } from './NaturalLanguagePreview';
import {
  applyBatchConfigPatch,
  buildBatchRulePatch,
  computeModuleTabsAfterToggle,
  emptySpecificRule,
  emptyTabRule,
  findTabRule,
  isModuleTabSelected,
  splitModuleInterfaceElements,
  tabElementRoleCode,
  upsertTabBatchRule,
  type BatchConfig,
} from '@/components/roles/role-form-utils';
import type { RoleBindingRule } from '@/types/role.types';
import type { InterfaceElement } from '@/types/interface-elements.types';

/** Props for {@link BatchActionRow}. */
interface BatchActionRowProps {
  icon: string;
  iconBg: string;
  label: string;
  checked: boolean;
  onToggle: (v: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}

/** One batch action row with optional nested scope. */
function BatchActionRow({
  icon,
  iconBg,
  label,
  checked,
  onToggle,
  disabled,
  children,
}: Readonly<BatchActionRowProps>) {
  return (
    <div
      className={`rounded-lg border transition-all ${checked ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 bg-white'}`}
    >
      <div className="flex items-center gap-3 p-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base ${iconBg}`}
        >
          {icon}
        </div>
        <span className="flex-1 text-sm font-medium text-gray-800">{label}</span>
        <Checkbox checked={checked} onCheckedChange={(v) => onToggle(!!v)} disabled={disabled} />
      </div>
      {children && <div className="px-3 pb-3 pt-0">{children}</div>}
    </div>
  );
}

/** Props for {@link ModuleAccessCard}. */
interface ModuleAccessCardProps {
  moduleLabel: string;
  moduleIcon: string | null;
  interfaceName: string;
  elements: InterfaceElement[];
  rules: RoleBindingRule[];
  onRulesChange: (rules: RoleBindingRule[]) => void;
  tabs?: string[] | null;
  onTabsChange?: (tabs: string[] | null) => void;
  disabled?: boolean;
  isEnabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
}

/** Empty batch-permission config. */
const EMPTY_BATCH: BatchConfig = {
  read: null,
  create: false,
  edit: false,
  editWriteScope: null,
  delete: false,
  deleteWriteScope: null,
};

/** Module access card for the role permissions builder. */
export function ModuleAccessCard({
  moduleLabel,
  moduleIcon,
  interfaceName,
  elements,
  rules,
  onRulesChange,
  tabs,
  onTabsChange,
  disabled,
  isEnabled = true,
  onEnabledChange,
}: Readonly<ModuleAccessCardProps>) {
  const [configMode, setConfigMode] = React.useState<'individual' | 'batch'>('individual');
  const [batchConfig, setBatchConfig] = React.useState<BatchConfig>(EMPTY_BATCH);

  // Split actions:
  // - perRoleActions: option-create-*, option-edit-*, option-delete-* (config.subtype ends in '-role')
  // - globalActions: export, reactivate, cancel, etc.
  const { availableActions, perRoleActions, globalActions, availableTabs } = React.useMemo(
    () => splitModuleInterfaceElements(elements),
    [elements],
  );

  const isRoleGroupModule = availableTabs.length > 0;
  const showTabToggle = availableTabs.length > 1;

  const handleTabToggle = (tabElementId: string) => {
    if (!onTabsChange || disabled) return;
    onTabsChange(
      computeModuleTabsAfterToggle(
        tabs,
        tabElementId,
        availableTabs.map((t) => t.elementId),
      ),
    );
  };

  const isTabSelected = (tabElementId: string): boolean => isModuleTabSelected(tabs, tabElementId);

  const anyTabSelected =
    availableTabs.length === 0 || availableTabs.some((t) => isTabSelected(t.elementId));

  const selectedTabs = React.useMemo(() => {
    if (!isRoleGroupModule) return [];
    return availableTabs.filter((t) => isTabSelected(t.elementId));
  }, [availableTabs, tabs, isRoleGroupModule]);

  const showConfigModeToggle = selectedTabs.length > 1;

  // Reset batch config when switching to batch mode or selected tabs change
  React.useEffect(() => {
    if (configMode === 'batch') {
      setBatchConfig(EMPTY_BATCH);
    }
  }, [configMode, selectedTabs.length]);

  const getTabRule = (tab: InterfaceElement): RoleBindingRule | null =>
    findTabRule(rules, tabElementRoleCode(tab.elementId));

  const handleUpdateTabRule = (tab: InterfaceElement, updates: Partial<RoleBindingRule>) => {
    const roleCode = tabElementRoleCode(tab.elementId);
    const existingRule = getTabRule(tab);
    if (existingRule) {
      onRulesChange(rules.map((r) => (r.id === existingRule.id ? { ...r, ...updates } : r)));
    } else {
      onRulesChange([...rules, emptyTabRule(roleCode, updates)]);
    }
  };

  /**
   * Convert conceptual batch config to concrete per-role rules and apply to all selected tabs.
   * Translates create/edit/delete booleans into option-{subtype}-{roleCode} action IDs.
   */
  const applyBatch = (cfg: BatchConfig) => {
    const updatedRules = [...rules];
    for (const tab of selectedTabs) {
      const roleCode = tabElementRoleCode(tab.elementId);
      upsertTabBatchRule(updatedRules, roleCode, {
        read: cfg.read,
        ...buildBatchRulePatch(cfg, perRoleActions, roleCode),
      });
    }
    onRulesChange(updatedRules);
  };

  const updateBatch = (patch: Partial<BatchConfig>) => {
    const next = applyBatchConfigPatch(batchConfig, patch);
    setBatchConfig(next);
    applyBatch(next);
  };

  const defaultRule = rules.find((r) => !r.entityFilter);
  const specificRules = rules.filter((r) => r.entityFilter);

  const handleUpdateRule = (ruleId: string, updatedRule: RoleBindingRule) =>
    onRulesChange(rules.map((r) => (r.id === ruleId ? updatedRule : r)));

  const handleDeleteRule = (ruleId: string) => onRulesChange(rules.filter((r) => r.id !== ruleId));

  const handleAddSpecificRule = () => onRulesChange([...rules, emptySpecificRule()]);

  const hasCreateRole = perRoleActions.some((a) => a.config?.subtype === 'create-role');
  const hasEditRole = perRoleActions.some((a) => a.config?.subtype === 'edit-role');
  const hasDeleteRole = perRoleActions.some((a) => a.config?.subtype === 'delete-role');

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {moduleIcon && (
            <DynamicIcon name={moduleIcon} className="w-6 h-6 text-gray-700 shrink-0" />
          )}
          <div>
            <h4 className="text-lg font-semibold text-gray-900">{moduleLabel}</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {rules.length} règle{rules.length > 1 ? 's configurées' : ' configurée'}
            </p>
          </div>
        </div>

        {onEnabledChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{isEnabled ? 'Activé' : 'Désactivé'}</span>
            <button
              type="button"
              onClick={() => onEnabledChange(!isEnabled)}
              disabled={disabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isEnabled ? 'bg-blue-600' : 'bg-gray-300'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}
      </div>

      {isEnabled && (
        <>
          <NaturalLanguagePreview rules={rules} moduleLabel={moduleLabel} />

          <div className="space-y-4">
            {isRoleGroupModule ? (
              <>
                {showTabToggle && (
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">
                      Types d'utilisateurs accessibles
                    </h5>
                    <div className="grid grid-cols-2 gap-2">
                      {availableTabs.map((tab) => (
                        <Checkbox
                          key={tab.elementId}
                          checked={isTabSelected(tab.elementId)}
                          onCheckedChange={() => handleTabToggle(tab.elementId)}
                          disabled={disabled}
                          label={tab.elementLabel}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Cochez les types d'utilisateurs que ce rôle peut voir et gérer.
                    </p>
                  </div>
                )}

                {anyTabSelected && selectedTabs.length > 0 && (
                  <div className="space-y-4">
                    {showConfigModeToggle && (
                      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setConfigMode('individual')}
                          className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            configMode === 'individual'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Par rôle
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfigMode('batch')}
                          className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            configMode === 'batch'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Ensemble
                        </button>
                      </div>
                    )}

                    {(!showConfigModeToggle || configMode === 'individual') && (
                      <div className="space-y-4">
                        {selectedTabs.map((tab) => (
                          <RoleTabPermCard
                            key={tab.id}
                            tab={tab}
                            rule={getTabRule(tab)}
                            perRoleActions={perRoleActions}
                            onUpdate={(updates) => handleUpdateTabRule(tab, updates)}
                            disabled={disabled}
                          />
                        ))}
                      </div>
                    )}

                    {showConfigModeToggle && configMode === 'batch' && (
                      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-5">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-900">
                            Ces paramètres s'appliquent à tous les types sélectionnés :{' '}
                            <span className="font-medium">
                              {selectedTabs.map((t) => t.elementLabel).join(', ')}
                            </span>
                          </p>
                        </div>

                        <div className="space-y-2">
                          <div className="text-sm font-semibold text-gray-700">
                            Périmètre de lecture
                          </div>
                          <ReadScopeBuilder
                            value={batchConfig.read}
                            onChange={(read) => updateBatch({ read })}
                            disabled={disabled}
                            label="Quelles données peut-il voir ?"
                            previewPrefix="Voit seulement"
                          />
                        </div>

                        {(hasCreateRole || hasEditRole || hasDeleteRole) && (
                          <div className="space-y-4">
                            <div className="text-sm font-semibold text-gray-700">
                              Actions disponibles
                            </div>

                            {hasCreateRole && (
                              <BatchActionRow
                                icon="➕"
                                iconBg="bg-green-100 text-green-600"
                                label="Peut créer"
                                checked={batchConfig.create}
                                onToggle={(v) => updateBatch({ create: v })}
                                disabled={disabled}
                              />
                            )}

                            {hasEditRole && (
                              <BatchActionRow
                                icon="✏️"
                                iconBg="bg-blue-100 text-blue-600"
                                label="Peut modifier"
                                checked={batchConfig.edit}
                                onToggle={(v) =>
                                  updateBatch({
                                    edit: v,
                                    editWriteScope: v
                                      ? (batchConfig.editWriteScope ?? batchConfig.read)
                                      : null,
                                  })
                                }
                                disabled={disabled}
                              >
                                {batchConfig.edit && (
                                  <ReadScopeBuilder
                                    value={batchConfig.editWriteScope}
                                    onChange={(ws) => updateBatch({ editWriteScope: ws })}
                                    disabled={disabled}
                                    label="Sur quelles données peut-il modifier ?"
                                    constrainedBy={batchConfig.read}
                                  />
                                )}
                              </BatchActionRow>
                            )}

                            {hasDeleteRole && (
                              <BatchActionRow
                                icon="🗑️"
                                iconBg="bg-red-100 text-red-600"
                                label="Peut supprimer"
                                checked={batchConfig.delete}
                                onToggle={(v) =>
                                  updateBatch({
                                    delete: v,
                                    deleteWriteScope: v
                                      ? (batchConfig.deleteWriteScope ?? batchConfig.read)
                                      : null,
                                  })
                                }
                                disabled={disabled}
                              >
                                {batchConfig.delete && (
                                  <ReadScopeBuilder
                                    value={batchConfig.deleteWriteScope}
                                    onChange={(ws) => updateBatch({ deleteWriteScope: ws })}
                                    disabled={disabled}
                                    label="Sur quelles données peut-il supprimer ?"
                                    constrainedBy={batchConfig.read}
                                  />
                                )}
                              </BatchActionRow>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Global actions (export, reactivate, etc.) — shown once at module level */}
                    {globalActions.length > 0 && (
                      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                        <div className="text-sm font-semibold text-gray-700">
                          Actions globales du module
                        </div>
                        <div className="space-y-2">
                          {globalActions.map((action) => {
                            const isChecked =
                              defaultRule?.actions?.includes(action.elementId) ?? false;
                            return (
                              <Checkbox
                                key={action.elementId}
                                label={action.elementLabel}
                                checked={isChecked}
                                disabled={disabled}
                                onCheckedChange={(v) => {
                                  const currentActions = defaultRule?.actions ?? [];
                                  const newActions = v
                                    ? [...currentActions, action.elementId]
                                    : currentActions.filter((id) => id !== action.elementId);
                                  if (defaultRule) {
                                    handleUpdateRule(defaultRule.id, {
                                      ...defaultRule,
                                      actions: newActions,
                                    });
                                  }
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {defaultRule && (
                  <RuleCard
                    key={defaultRule.id}
                    rule={defaultRule}
                    interfaceName={interfaceName}
                    availableActions={availableActions}
                    onUpdate={(updated) => handleUpdateRule(defaultRule.id, updated)}
                    onDelete={() => handleDeleteRule(defaultRule.id)}
                    disabled={disabled}
                    isDefault
                  />
                )}

                {specificRules.map((rule) => (
                  <RuleCard
                    key={rule.id}
                    rule={rule}
                    interfaceName={interfaceName}
                    availableActions={availableActions}
                    onUpdate={(updated) => handleUpdateRule(rule.id, updated)}
                    onDelete={() => handleDeleteRule(rule.id)}
                    disabled={disabled}
                    isDefault={false}
                  />
                ))}

                <button
                  type="button"
                  onClick={handleAddSpecificRule}
                  disabled={disabled}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Ajouter une règle spécifique</span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
